import React, { useState, useEffect } from 'react';
import { billingAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { CurrencyDollar, Clock, CalendarBlank, PencilSimple, FunnelSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format, subDays } from 'date-fns';

const ClientBilling = ({ clientId, clientData, userRole, onRatesUpdated }) => {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isRateOpen, setIsRateOpen] = useState(false);
  const [rateForm, setRateForm] = useState({
    standard_rate: clientData?.standard_rate || '',
    overtime_rate: clientData?.overtime_rate || '',
    weekend_rate: clientData?.weekend_rate || '',
    holiday_rate: clientData?.holiday_rate || ''
  });

  useEffect(() => { loadBilling(); }, [clientId]);

  useEffect(() => {
    setRateForm({
      standard_rate: clientData?.standard_rate || '',
      overtime_rate: clientData?.overtime_rate || '',
      weekend_rate: clientData?.weekend_rate || '',
      holiday_rate: clientData?.holiday_rate || ''
    });
  }, [clientData]);

  const loadBilling = async () => {
    setLoading(true);
    try {
      const res = await billingAPI.getClientBilling(clientId, { start_date: startDate, end_date: endDate });
      setBilling(res.data);
    } catch (error) { console.error('Error loading billing:', error); }
    finally { setLoading(false); }
  };

  const handleUpdateRates = async (e) => {
    e.preventDefault();
    try {
      await billingAPI.updateRates(clientId, {
        standard_rate: parseFloat(rateForm.standard_rate) || 0,
        overtime_rate: rateForm.overtime_rate ? parseFloat(rateForm.overtime_rate) : null,
        weekend_rate: rateForm.weekend_rate ? parseFloat(rateForm.weekend_rate) : null,
        holiday_rate: rateForm.holiday_rate ? parseFloat(rateForm.holiday_rate) : null
      });
      toast.success('Billing rates updated');
      setIsRateOpen(false);
      loadBilling();
      if (onRatesUpdated) onRatesUpdated();
    } catch (error) { toast.error('Failed to update rates'); }
  };

  return (
    <div className="space-y-6">
      {/* Rate Card */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">Billing</h2>
          <p className="text-sm text-muted-foreground">Pay rates and billing history</p>
        </div>
        {(userRole === 'supervisor' || userRole === 'super_admin') && (
          <Dialog open={isRateOpen} onOpenChange={setIsRateOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="rounded-xl gap-2" data-testid="edit-rates-btn">
                <PencilSimple weight="bold" className="w-4 h-4" /> Set Rates
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Set Billing Rates</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateRates} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Standard Hourly Rate ($) *</Label>
                  <Input type="number" step="0.01" min="0" value={rateForm.standard_rate} onChange={(e) => setRateForm({ ...rateForm, standard_rate: e.target.value })} placeholder="25.00" required className="rounded-xl" data-testid="rate-standard-input" />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Rate ($) <span className="text-xs text-muted-foreground">(default: 1.5x standard)</span></Label>
                  <Input type="number" step="0.01" min="0" value={rateForm.overtime_rate} onChange={(e) => setRateForm({ ...rateForm, overtime_rate: e.target.value })} placeholder="37.50" className="rounded-xl" data-testid="rate-overtime-input" />
                </div>
                <div className="space-y-2">
                  <Label>Weekend Rate ($) <span className="text-xs text-muted-foreground">(default: 1.25x standard)</span></Label>
                  <Input type="number" step="0.01" min="0" value={rateForm.weekend_rate} onChange={(e) => setRateForm({ ...rateForm, weekend_rate: e.target.value })} placeholder="31.25" className="rounded-xl" data-testid="rate-weekend-input" />
                </div>
                <div className="space-y-2">
                  <Label>Holiday Rate ($) <span className="text-xs text-muted-foreground">(default: office holiday multiplier × standard)</span></Label>
                  <Input type="number" step="0.01" min="0" value={rateForm.holiday_rate} onChange={(e) => setRateForm({ ...rateForm, holiday_rate: e.target.value })} placeholder="45.00" className="rounded-xl" data-testid="rate-holiday-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsRateOpen(false)}>Cancel</Button>
                  <Button type="submit" className="flex-1 rounded-xl" data-testid="save-rates-btn">Save Rates</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Current Rates */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="rounded-2xl border border-border">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Standard Rate</p>
            <p className="font-heading text-2xl font-semibold mt-1 text-green-700">
              ${(clientData?.standard_rate || 0).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span>
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-border">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Overtime Rate</p>
            <p className="font-heading text-2xl font-semibold mt-1 text-orange-700">
              ${(clientData?.overtime_rate || (clientData?.standard_rate || 0) * 1.5).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span>
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-border">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Weekend Rate</p>
            <p className="font-heading text-2xl font-semibold mt-1 text-purple-700">
              ${(clientData?.weekend_rate || (clientData?.standard_rate || 0) * 1.25).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span>
            </p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border border-border" data-testid="rate-holiday-card">
          <CardContent className="p-4 text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">Holiday Rate</p>
            <p className="font-heading text-2xl font-semibold mt-1 text-rose-700">
              ${(clientData?.holiday_rate || (clientData?.standard_rate || 0) * 1.5).toFixed(2)}<span className="text-sm font-normal text-muted-foreground">/hr</span>
            </p>
            {!clientData?.holiday_rate && (
              <p className="text-[10px] text-muted-foreground mt-1">using office multiplier</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Date Filter */}
      <Card className="rounded-2xl border border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="space-y-1 flex-1">
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl" data-testid="client-billing-start" />
            </div>
            <div className="space-y-1 flex-1">
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl" data-testid="client-billing-end" />
            </div>
            <Button className="rounded-xl gap-2" onClick={loadBilling} disabled={loading} data-testid="client-billing-filter">
              <FunnelSimple weight="bold" className="w-4 h-4" /> {loading ? 'Loading...' : 'Filter'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Billing Summary */}
      {billing && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-2xl border border-border" data-testid="client-billing-total">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center"><CurrencyDollar weight="duotone" className="w-5 h-5 text-green-600" /></div>
                  <div><p className="text-xs text-muted-foreground">Total Amount</p><p className="font-heading text-xl font-semibold">${billing.total_amount.toFixed(2)}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center"><Clock weight="duotone" className="w-5 h-5 text-blue-600" /></div>
                  <div><p className="text-xs text-muted-foreground">Total Hours</p><p className="font-heading text-xl font-semibold">{billing.total_hours}</p></div>
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-border">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center"><CalendarBlank weight="duotone" className="w-5 h-5 text-primary" /></div>
                  <div><p className="text-xs text-muted-foreground">Visits</p><p className="font-heading text-xl font-semibold">{billing.total_visits}</p></div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Line Items */}
          <Card className="rounded-2xl border border-border" data-testid="client-billing-items">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Visit Billing Details</CardTitle>
            </CardHeader>
            <CardContent>
              {billing.line_items.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground text-sm">No billable visits in this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Caregiver</TableHead>
                        <TableHead className="text-right">Hours</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {billing.line_items.map((item, i) => (
                        <TableRow key={item.visit_id} className={i % 2 === 0 ? '' : 'bg-muted/30'}>
                          <TableCell className="text-sm">{item.date} {item.time}</TableCell>
                          <TableCell className="text-sm">{item.caregiver_name}</TableCell>
                          <TableCell className="text-sm text-right">
                            {item.hours}
                            {item.overtime_hours > 0 && (
                              <div className="text-[10px] text-muted-foreground leading-tight">
                                {item.regular_hours} reg + {item.overtime_hours} OT
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-sm text-right">${item.rate.toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge className={`rounded-full text-xs ${item.rate_type?.includes('overtime') ? 'bg-orange-100 text-orange-800' : item.rate_type === 'weekend' ? 'bg-purple-100 text-purple-800' : 'bg-green-100 text-green-800'}`}>{item.rate_type}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-right font-medium">${item.amount.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default ClientBilling;
